import React, { useState } from 'react';
import { WarningCallOut } from '@grnhse/seedling/lib/birch/components/call_out';
import { Body } from '@grnhse/seedling/lib/birch/typography';
import { Stack } from '@grnhse/seedling/lib/birch/layouts/stack_v2';
import { withLocalePrefix } from '../shared/utils/translation';
import ScopeFamilySection from 'shared/components/scope_tree/scope_family_section';
import { ScopeFamily } from 'shared/components/scope_tree/types';
import AuthorizeAction from './authorize_action';
import ConsentAction from './consent_action';
import Checkbox from '@grnhse/seedling/lib/birch/components/checkbox';
import ConsentHeader from './consent_header';
import LoggedInAs from './logged_in_as';
import type { LoggedInAsProps } from './logged_in_as';

const t = withLocalePrefix('authorization.authorization_form');

type Props = {
  name: string;
  families: ScopeFamily[];
  accountSettingsUrl: string;
  companyLogoUrl: string;
  denyUrl: string;
  isOidcFlow?: boolean;
  isSiteAdmin?: boolean;
  onlyServiceAccountAuthorization?: boolean;
  emailValidation?: {
    email: string;
    error: string;
  };
  loggedInAs?: LoggedInAsProps | null;
};

const AUTHORIZE_PATH = '/authorize';
const CONSENT_PATH = '/oauth/consent';

export default function AuthorizationForm({
  name,
  families,
  accountSettingsUrl,
  companyLogoUrl,
  denyUrl,
  isOidcFlow,
  isSiteAdmin,
  onlyServiceAccountAuthorization,
  emailValidation,
  loggedInAs,
}: Props) {
  const pathName = window.location.pathname;
  const [useServiceAccount, setUseServiceAccount] = useState(false);

  const onCancel = () => {
    window.location.href = denyUrl;
  };

  return (
    <Stack>
      <ConsentHeader name={name} companyLogoUrl={companyLogoUrl} />
      {pathName === CONSENT_PATH && loggedInAs && (
        <LoggedInAs {...loggedInAs} />
      )}
      {families.length > 0 && (
        <Stack spacing={2}>
          <Body>{t('scope_description')}</Body>
          {families.map((family) => (
            <ScopeFamilySection key={family.key} family={family} readOnly />
          ))}
        </Stack>
      )}
      {pathName === CONSENT_PATH &&
        isSiteAdmin &&
        !isOidcFlow &&
        (onlyServiceAccountAuthorization ? (
          <WarningCallOut
            data-test={'service-account-only-notice'}
            headerText={t('service_account_only_header')}
            bodyText={t('service_account_only_notice')}
          />
        ) : (
          <Stack spacing={2}>
            <input
              type={'hidden'}
              name={'authorize_service_account'}
              id={'authorize_service_account'}
              value={useServiceAccount ? 'true' : 'false'}
            />
            <Checkbox
              data-test={'use-service-account-checkbox'}
              checked={useServiceAccount}
              onChange={(checked: boolean) => {
                setUseServiceAccount(checked);
              }}
              label={t('use_service_account_label')}
              value={'use_service_account'}
              helperText={t('use_service_account_helper')}
            />
          </Stack>
        ))}
      {pathName === AUTHORIZE_PATH && (
        <AuthorizeAction
          accountSettingsUrl={accountSettingsUrl}
          error={emailValidation?.error}
          email={emailValidation?.email}
          onCancel={onCancel}
        />
      )}
      {pathName === CONSENT_PATH && (
        <ConsentAction
          accountSettingsUrl={accountSettingsUrl}
          onCancel={onCancel}
          submitLabel={
            onlyServiceAccountAuthorization
              ? t('authorize_service_account_cta')
              : undefined
          }
        />
      )}
    </Stack>
  );
}
